import React, { useEffect, useRef, useState } from "react";

import {
  Container,
  Typography,
  Button,
  Card,
  Divider,
  CardContent,
  Select,
  MenuItem,
  Paper,
  Grid,
  Link,
  Stack,
  Chip,
  Box,
  useMediaQuery,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import img1 from "./teatro.webp";
import img2 from "./img2.jpg";
import img3 from "./img3.webp";
import img4 from "./img4.jpg";
import img5 from "./img5.jpg";
import img6 from "./img6.jpg";
import img7 from "./img7.jpg";
import img8 from "./img8.jpg";
import img9 from "./img9.png";
import MuseumInfo from "./museum-info";
import {
  Apartment,
  Email,
  LocalPhone,
  Print,
  Public,
  Place,
  MarkunreadMailbox,
  SupervisorAccount,
  AccessTime,
  AttachMoney,
} from "@mui/icons-material";
import MailIcon from "@mui/icons-material/Email";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import PrintIcon from "@mui/icons-material/Print";
import LanguageIcon from "@mui/icons-material/Language";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import VirtualTour from "./VirtualTour";
import { ReactPhotoSphereViewer } from "react-photo-sphere-viewer";
import BottomMenu from "./BottomMenu";
import { cardsTxtEN, cardsTxtEs, cardsTxtFr } from "./const";
import type { SiteInfo } from "../types/site";
// import audioFile from "./audio.mp3";

const IconBox: React.FC<{ icon: React.ReactElement }> = ({ icon }) => (
  <Box className="flex items-center justify-center h-12 w-12 rounded-xl bg-teal-600 text-white">
    {icon}
  </Box>
);

const data = {
  es: {
    title: "Museo Nacional de Costa Rica",
    descriptions: cardsTxtEs,
    curiosities: [
      "El teatro podía albergar a más de 6,000 personas.",
      "Fue redescubierto en 1790 durante una excavación.",
    ],
    audio: "/audio/es.mp3",
    imageTimeline: [
      { time: 0, src: img1 },
      { time: 5, src: img2 },
      { time: 10, src: img3 },
    ],
  },
  en: {
    title: "National Museum of Costa Rica",
    descriptions: cardsTxtEN,
    curiosities: [
      "The theater could hold more than 6,000 people.",
      "It was rediscovered in 1790 during an excavation.",
    ],
    audio: "/audio/en.mp3",
    imageTimeline: [
      { time: 0, src: img1 },
      { time: 5, src: img2 },
      { time: 10, src: img3 },
    ],
  },
  fr: {
    title: "Musée National du Costa Rica",
    descriptions: cardsTxtFr,
    curiosities: [
      "Le théâtre pouvait accueillir plus de 6 000 personnes.",
      "Il a été redécouvert en 1790 lors d'une excavation.",
    ],
    audio: "/audio/fr.mp3",
    imageTimeline: [
      { time: 0, src: img1 },
      { time: 5, src: img2 },
      { time: 10, src: img3 },
    ],
  },
};

const gallery = [
  {
    type: 0,
    element: img1,
  },
  {
    type: 2,
    element: img2,
  },
  {
    type: 1,
    element: img3,
  },
  {
    type: 0,
    element: img4,
  },
  {
    type: 1,
    element: img5,
  },
  {
    type: 0,
    element: img6,
  },
  {
    type: 0,
    element: img7,
  },
  {
    type: 2,
    element: img8,
  },
  {
    type: 0,
    element: img9,
  },
];

// const gallery = [img1, img2, img3];

type HistoricalSitePageProps = {
  onBack?: () => void;
  site?: SiteInfo | null;
};

export default function HistoricalSitePage({
  onBack,
  site,
}: HistoricalSitePageProps) {
  const [lang, setLang] = useState("es");
  const [content, setContent]: any = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentImage, setCurrentImage] = useState(img1);
  const [currentImageType, setCurrentImageType] = useState(0);
  const audioRef = useRef(new Audio("/audio.mp3"));
  const [textIndex, setTextIndex]: any = useState(0);
  const [menuItem, setMenuItem] = useState(0);
  const isTablet = useMediaQuery("(max-width:1024px)");
  const isMobile = useMediaQuery("(max-width:640px)");
  const viewerHeight = isMobile ? "50vh" : isTablet ? "60vh" : "100vh";
  const imageHeightClass = isMobile
    ? "h-[50vh]"
    : isTablet
    ? "h-[60vh]"
    : "h-full";

  const handlePrev = () => {
    setTextIndex((prev: any) => (prev > 0 ? prev - 1 : prev));
  };

  const handleNext = () => {
    setTextIndex((prev: any) => (prev < 2 ? prev + 1 : prev));
  };

  const handleCLickImageGallery = (index: number) => {
    setCurrentImage(gallery[index].element);

    if (gallery[index].type === 1) {
      setCurrentImageType(1);
    } else {
      setCurrentImageType(0);
    }
  };

  const getTypeLabel = (type: number) => {
    switch (type) {
      case 0:
        return "Imagen";
      case 1:
        return "Imagen 360°";
      case 2:
        return "Video";
      case 3:
        return "Audio";
      default:
        return "Desconocido";
    }
  };

  const handleMiniGallery = () => {
    return (
      <Paper elevation={3} className="mt-2 p-4 sm:p-5 lg:ml-5">
        <section className="w-full text-left">
          <div className="mini-gallery-container grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {[gallery[0], gallery[1], gallery[2], gallery[3]].map(
              (n, index) => (
                <Box
                  key={index + "card"}
                  sx={{
                    position: "relative",
                    width: "100%",
                    height: { xs: 120, sm: 140, md: 160 },
                  }}
                >
                  <img
                    onClick={() => handleCLickImageGallery(index)}
                    src={n.element}
                    alt={`Elemento ${index}`}
                    className="rounded-lg shadow object-cover w-full h-full"
                  />
                  <Chip
                    label={getTypeLabel(n.type)}
                    size="small"
                    color="primary"
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      backgroundColor: "rgba(0,0,0,0.7)",
                      color: "#fff",
                      fontSize: "0.7rem",
                    }}
                  />
                </Box>
              )
            )}
          </div>
        </section>
      </Paper>
    );
  };

  const handleHomeUI = () => {
    const pageTitle = site?.nombre ?? content.title;

    return (
      <>
        <></>
        <section className="max-w-3xl mx-auto mb-6">
          <Typography variant="h2" className="font-bold">
            {pageTitle}
          </Typography>
        </section>

        <div style={{ textAlign: "left" }} className="w-full p-5">
          <Typography variant="h5" className="font-bold">
            {content.descriptions[textIndex].title}
          </Typography>
          <Typography
            style={{ textAlign: "justify", marginBottom: "10px" }}
            variant="body1"
          >
            {content.descriptions[textIndex].description}
          </Typography>

          <div className="flex justify-between items-center mt-6">
            <button
              onClick={handlePrev}
              disabled={textIndex === 0}
              className={`flex items-center gap-2 px-4 py-2 rounded ${
                textIndex === 0
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-gray-300 text-black hover:bg-gray-400"
              }`}
            >
              <ArrowBackIcon />
              Anterior
            </button>

            <button
              onClick={handleNext}
              disabled={textIndex === 2}
              className={`flex items-center gap-2 px-4 py-2 rounded ${
                textIndex === 2
                  ? "bg-blue-200 text-white cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
            >
              Siguiente
              <ArrowForwardIcon />
            </button>
          </div>
        </div>

        {handleMiniGallery()}
      </>
    );
  };

  const hadleMenu = (value: any) => {
    setMenuItem(value);
  };

  const handleInfoUI = () => {
    return (
      <Paper
        elevation={2}
        sx={{ p: 4, borderRadius: 2, maxHeight: "65vh", overflowY: "auto" }}
      >
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Información General
        </Typography>

        <Stack spacing={3} divider={<Divider flexItem />}>
          {/* Contacto */}
          <Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              <MailIcon fontSize="small" sx={{ mr: 1 }} />
              Contacto
            </Typography>
            <Stack spacing={1}>
              <Typography>
                <strong>Email:</strong>{" "}
                <Link href="mailto:informacion@museocostarica.go.cr">
                  informacion@museocostarica.go.cr
                </Link>
              </Typography>
              <Typography>
                <strong>Email alternativo:</strong>{" "}
                <Link href="mailto:direccion@museocostarica.go.cr">
                  direccion@museocostarica.go.cr
                </Link>
              </Typography>
              <Typography>
                <LocalPhoneIcon fontSize="small" sx={{ mr: 1 }} />
                +506 2211 5700
              </Typography>

              <Typography>
                <LanguageIcon fontSize="small" sx={{ mr: 1 }} />
                <Link
                  href="https://www.museocostarica.go.cr/"
                  target="_blank"
                  rel="noopener"
                >
                  www.museocostarica.go.cr
                </Link>
              </Typography>
            </Stack>
          </Box>

          {/* Ubicación */}
          <Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              <LocationOnIcon fontSize="small" sx={{ mr: 1 }} />
              Ubicación
            </Typography>
            <Typography>San José, San José, Carmen</Typography>
            <Typography>
              <strong>Apartado postal:</strong> 749-1000 San José, Costa Rica
            </Typography>
          </Box>

          {/* Atención */}
          <Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              <AccessTimeIcon fontSize="small" sx={{ mr: 1 }} />
              Atención al Público
            </Typography>
            <Typography>
              <strong>Directora:</strong> María Ifigenia Quintanilla Jiménez
            </Typography>
            <Typography>
              <strong>Horario:</strong> Martes a sábado de 8:30 a.m. a 4:30 p.m.
              / Domingos de 9 a.m. a 4:30 p.m.
            </Typography>
          </Box>

          {/* Costos */}
          <Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              <MonetizationOnIcon fontSize="small" sx={{ mr: 1 }} />
              Costo del Uso del Espacio
            </Typography>
            <Typography>
              • Entrada gratuita: estudiantes con carné, ciudadanos de oro con
              cédula, menores de 12 años
            </Typography>
            <Typography>• Nacionales: ₡2.500</Typography>
            <Typography>
              • Domingos: entrada gratuita para nacionales
            </Typography>
            <Typography>
              • Extranjeros: $11 / Estudiantes extranjeros con carné: $6
            </Typography>
          </Box>
        </Stack>
      </Paper>
    );
  };

  const handleGalleryUI = () => {
    return (
      <>
        <Typography variant="h2" className="font-bold">
          Galería
        </Typography>
        {handleImageGrid()}
      </>
    );
  };

  const componentValidationHere = () => {
    switch (menuItem) {
      case 0:
        return handleHomeUI();
      case 1:
        return handleInfoUI();
      case 2:
        return handleGalleryUI();
      default:
        return null;
    }
  };

  const handleInfoCard = () => {
    return (
      <section className="max-w-3xl mx-auto mb-6 p-5">
        <Typography variant="h6" className="mb-2">
          <strong>Curiosidades</strong>
        </Typography>
        <Grid>
          <Paper elevation={1} className="p-4 flex gap-4">
            <IconBox icon={<Email />} />
            <Box>
              <Typography fontWeight={600}>Correo electrónico</Typography>
              <Typography variant="body2">
                informacion@museocostarica.go.cr
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </section>
    );
  };

  const handlePhotoViewerMode = () => {
    return (
      <AnimatePresence mode="wait">
        <ReactPhotoSphereViewer
          src={currentImage}
          height={viewerHeight}
          width={"100%"}
        ></ReactPhotoSphereViewer>
      </AnimatePresence>
    );
  };

  const fetchContent = async (lang: any) => {
    const json: any = data;
    const audio = new Audio(json[lang].audio);
    audioRef.current = audio;
    setContent(json[lang]);
  };

  useEffect(() => {
    fetchContent(lang);
  }, [lang]);

  const handleToggleAudio = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.currentTime = 0;
      audio.play();
      setIsPlaying(true);

      audio.onended = () => {
        setIsPlaying(false);
      };
    }
  };

  const handleImageGrid = () => {
    const imageItems = gallery;

    return (
      <Paper elevation={3} className="mt-2 p-4 sm:p-5 lg:ml-5">
        <section className="w-full text-left">
          <div className="image-gallery grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {imageItems.map((n, index) => (
              <Box
                key={index + "img"}
                sx={{
                  position: "relative",
                  width: "100%",
                  height: { xs: 120, sm: 140, md: 160 },
                }}
              >
                <img
                  onClick={() => handleCLickImageGallery(index)}
                  src={n.element}
                  alt={`Imagen ${index}`}
                  className="rounded-lg shadow object-cover w-full h-full"
                />
                <Chip
                  label={getTypeLabel(n.type)}
                  size="small"
                  color="primary"
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    backgroundColor: "rgba(0,0,0,0.6)",
                    color: "#fff",
                    fontSize: "0.7rem",
                  }}
                />
              </Box>
            ))}
          </div>
        </section>
      </Paper>
    );
  };

  useEffect(() => {
    if (!audioRef.current || !content) return;

    const audio = audioRef.current;

    const handleTimeUpdate = () => {
      const time = Math.floor(audio.currentTime);
      const nextImage = [...content.imageTimeline]
        .reverse()
        .find((img) => time >= img.time);
      if (nextImage && nextImage.src !== currentImage) {
        setCurrentImage(nextImage.src);
      }
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    return () => audio.removeEventListener("timeupdate", handleTimeUpdate);
  }, [content, currentImage]);

  if (!content) return <p>Cargando contenido...</p>;

  const renderAudioControls = (extraClasses = "") => (
    <div className={`flex flex-col gap-4 ${extraClasses}`}>
      <Button
        fullWidth
        style={{ backgroundColor: "black" }}
        variant="contained"
        onClick={handleToggleAudio}
      >
        {isPlaying ? "⏸️ Pausar" : "🔊 Escuchar"}
      </Button>
      <Select
        fullWidth
        value={lang}
        onChange={(e) => setLang(e.target.value)}
        style={{ backgroundColor: "white" }}
      >
        <MenuItem value="es">Español</MenuItem>
        <MenuItem value="en">English</MenuItem>
        <MenuItem value="fr">Français</MenuItem>
      </Select>
    </div>
  );

  return (
    <main className="min-h-screen bg-gray-100 text-gray-800">
      <div className="min-h-screen w-full flex flex-col lg:flex-row">
        <div className="relative w-full lg:w-1/2 min-h-[320px] lg:min-h-screen">
          <section className="h-full">
            {currentImageType === 1 ? (
              handlePhotoViewerMode()
            ) : (
              <motion.img
                key={currentImage} // cambia la imagen cuando cambia la key
                src={currentImage}
                alt={site?.nombre ?? content.title}
                className={`mx-auto rounded-xl shadow-md w-full object-cover ${imageHeightClass}`}
                style={{ maxHeight: viewerHeight }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1 }}
              />
            )}
          </section>

          {!isTablet && (
            <section className="absolute top-1/2 left-6 z-10 flex justify-start">
              {renderAudioControls("w-full max-w-xs items-start")}
            </section>
          )}
          {isTablet && (
            <section className="w-full px-4 py-6 flex justify-start">
              {renderAudioControls("max-w-xs w-full")}
            </section>
          )}
        </div>

        <div className="relative w-full lg:w-1/2 p-4 lg:p-6 pb-28 lg:pb-6 flex flex-col max-h-screen">
          {onBack && (
            <div className="mb-4 flex justify-start lg:justify-end">
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={onBack}
              >
                Volver al inicio
              </Button>
            </div>
          )}
          <div className="flex-1 overflow-y-auto pr-1">
            {componentValidationHere()}
          </div>
          <BottomMenu onItemClick={hadleMenu} />
        </div>
      </div>
    </main>
  );
}
